import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { dataV2Api } from "apis/dataV2/dataV2Api";
import { procoreApi } from "apis/procore/procoreApi";
import { bottomToolbarReducer } from "components/toolbars/mobile/slice";
import { alignmentReducer } from "features/alignment/slice";
import { arcgisSlice } from "features/arcgis";
import { arcgisApi } from "features/arcgis/arcgisApi";
import { areaReducer } from "features/area";
import { bimCollabApi, bimCollabReducer } from "features/bimCollab";
import { bimTrackApi, bimTrackReducer } from "features/bimTrack";
import { bookmarksReducer } from "features/bookmarks";
import { clashReducer } from "features/clash";
import { clashApi } from "features/clash/clashApi";
import { coordinatesReducer } from "features/coordinates";
import { crossSectionReducer } from "features/crossSection/slice";
import { deviationsReducer } from "features/deviations";
import { ditioApi } from "features/ditio";
import { ditioReducer } from "features/ditio";
import { formsApi, formsReducer } from "features/forms";
import { groupsReducer } from "features/groups";
import { heightProfileReducer } from "features/heightProfile";
import { imagesReduces } from "features/images";
import { jiraApi, jiraReducer } from "features/jira";
import { manholeReducer } from "features/manhole";
import { markersReducer } from "features/markers";
import { measureReducer } from "features/measure";
import { miniMapReducer } from "features/miniMap";
import { mobileMeasureReducer } from "features/mobileMeasure";
import { modelTreeReducer } from "features/modelTree/slice";
import { myLocationReducer } from "features/myLocation";
import { offlineReducer } from "features/offline";
import { omega365Reducer } from "features/omega365";
import { orthoCamReducer } from "features/orthoCam";
import { clippingOutlineLaserReducer } from "features/outlineLaser";
import { pimsReducer } from "features/pims";
import { pointLineReducer } from "features/pointLine";
import { pointVisualizationReducer } from "features/pointVisualization";
import { propertiesReducer } from "features/properties/slice";
import { propertyTreeApi, propertyTreeReducer } from "features/propertyTree";
import { renderReducer } from "features/render";
import { sectionPlaneReducer } from "features/sectionPlane";
import { selectionBasketReducer } from "features/selectionBasket";
import { undoRedoReducer } from "features/undoRedo";
import { xsiteManageApi, xsiteManageReducer } from "features/xsiteManage";
import { authReducer } from "slices/authSlice";
import { explorerReducer } from "slices/explorer";
import { featureTogglesReducer } from "slices/featureToggles";

const rootReducer = combineReducers({
    explorer: explorerReducer,
    render: renderReducer,
    auth: authReducer,
    groups: groupsReducer,
    bookmarks: bookmarksReducer,
    images: imagesReduces,
    forms: formsReducer,
    [formsApi.reducerPath]: formsApi.reducer,
    deviations: deviationsReducer,
    measure: measureReducer,
    area: areaReducer,
    orthoCam: orthoCamReducer,
    manhole: manholeReducer,
    pointLine: pointLineReducer,
    myLocation: myLocationReducer,
    bimCollab: bimCollabReducer,
    heightProfile: heightProfileReducer,
    clippingOutline: clippingOutlineLaserReducer,
    coordinates: coordinatesReducer,
    selectionBasket: selectionBasketReducer,
    properties: propertiesReducer,
    offline: offlineReducer,
    pims: pimsReducer,
    propertyTree: propertyTreeReducer,
    [propertyTreeApi.reducerPath]: propertyTreeApi.reducer,
    [bimCollabApi.reducerPath]: bimCollabApi.reducer,
    bimTrack: bimTrackReducer,
    [bimTrackApi.reducerPath]: bimTrackApi.reducer,
    ditio: ditioReducer,
    [ditioApi.reducerPath]: ditioApi.reducer,
    jira: jiraReducer,
    [jiraApi.reducerPath]: jiraApi.reducer,
    xsiteManage: xsiteManageReducer,
    [xsiteManageApi.reducerPath]: xsiteManageApi.reducer,
    [dataV2Api.reducerPath]: dataV2Api.reducer,
    [procoreApi.reducerPath]: procoreApi.reducer,
    arcgis: arcgisSlice.reducer,
    omega365: omega365Reducer,
    [arcgisApi.reducerPath]: arcgisApi.reducer,
    [clashApi.reducerPath]: clashApi.reducer,
    clash: clashReducer,
    pointVisualization: pointVisualizationReducer,
    crossSection: crossSectionReducer,
    alignment: alignmentReducer,
    modelTree: modelTreeReducer,
    undoRedo: undoRedoReducer,
    bottomToolbar: bottomToolbarReducer,
    markers: markersReducer,
    mobileMeasure: mobileMeasureReducer,
    sectionPlane: sectionPlaneReducer,
    miniMap: miniMapReducer,
    featureToggles: featureTogglesReducer,
});

export const createStore = () => {
    return configureStore({
        reducer: rootReducer,
        devTools: import.meta.env.NODE_ENV === "development",
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [
                        "undoRedo/push",
                        "undoRedo/undo",
                        "undoRedo/redo",
                        "markers/createCollection",
                        "markers/addMarker",
                        "markers/updateMarker",
                        "markers/updateCollectionConfig",
                        "markers/updateCreationConfig",
                        "miniMap/setDrawingData",
                        "bookmarks/setConfirmLargeBookmark",
                    ],
                    ignoredPaths: [
                        "miniMap",
                        "undoRedo",
                        /^markers\.collections\..*\.config/,
                        /^markers\.collections\..*\.markerCreationConfig/,
                        "bookmarks.confirmLargeBookmark",
                    ],
                    ignoredActionPaths: ["meta.arg", "meta.baseQueryMeta", "meta.view", "meta.publicIdsToObjectIds"],
                },
            })
                .concat(bimCollabApi.middleware)
                .concat(bimTrackApi.middleware)
                .concat(ditioApi.middleware)
                .concat(formsApi.middleware)
                .concat(jiraApi.middleware)
                .concat(xsiteManageApi.middleware)
                .concat(dataV2Api.middleware)
                .concat(procoreApi.middleware)
                .concat(propertyTreeApi.middleware)
                .concat(arcgisApi.middleware)
                .concat(clashApi.middleware),
    });
};

export const store = createStore();

setupListeners(store.dispatch);

export type AppStore = typeof store;
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
